import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * ProtectedRoute Component
 * Bảo vệ route dựa trên permissions hoặc roles
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component con cần render
 * @param {string|string[]} props.permission - Permission(s) yêu cầu
 * @param {string|string[]} props.role - Role(s) yêu cầu
 * @param {boolean} props.requireAdmin - Yêu cầu admin role
 * @param {React.ReactNode} props.fallback - Component hiển thị khi không có quyền
 */
function ProtectedRoute({ 
  children, 
  permission, 
  role, 
  requireAdmin = false,
  fallback = null 
}) {
  const { user, loading, hasPermission, hasRole, isAdmin } = useAuth();

  if (loading) {
    return <LoadingSpinner text="Đang kiểm tra quyền truy cập..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Kiểm tra admin requirement
  if (requireAdmin && !isAdmin()) {
    return fallback || (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <h4>Không có quyền truy cập, liên hệ với admin để được cấp quyền</h4>
          <p>Bạn cần quyền admin để truy cập trang này.</p>
        </div>
      </div>
    );
  }

  // Kiểm tra role requirement
  if (role) {
    const requiredRoles = Array.isArray(role) ? role : [role];
    const hasRequiredRole = requiredRoles.some(r => hasRole(r));
    
    if (!hasRequiredRole) {
      return fallback || (
        <div className="container mt-4">
          <div className="alert alert-warning">
            <h4>Không có quyền truy cập, liên hệ với admin để được cấp quyền</h4>
            <p>Bạn cần một trong các vai trò: {requiredRoles.join(', ')}</p>
          </div>
        </div>
      );
    }
  }

  // Kiểm tra permission requirement
  if (permission) {
    const requiredPermissions = Array.isArray(permission) ? permission : [permission];
    const hasRequiredPermission = requiredPermissions.some(p => hasPermission(p));
    
    if (!hasRequiredPermission) {
      return fallback || (
        <div className="container mt-4">
          <div className="alert alert-warning">
            <h4>Không có quyền truy cập, liên hệ với admin để được cấp quyền</h4>
         
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
